import React, { useState, useRef, useEffect } from 'react';
import './MembershipAndVisits.css';
import { FaIdCard, FaUserCheck, FaTicketAlt, FaSignInAlt, FaCalendarCheck, FaCaretDown, FaCaretUp } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import BuyPassModal from '../../WhatWeGot/Pricing/BuyPassModal/BuyPassModal';
import MakeRecurringMembershipModal from './MakeRecurringMembershipModal/MakeRecurringMembershipModal';

// Coming Soon Mode: When true, pass options link is disabled and "coming soon" message is shown.
// Set to false when Dog Passes page is live and route is re-enabled in App.js.
const PASS_OPTIONS_COMING_SOON = true;
const stripePromise = (() => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    console.error('Stripe publishable key is missing. Please check your environment variables.');
    return null;
  }
  return loadStripe(key);
})();

// Helper function to get emoji and formatted text for membership types
const getMembershipDisplay = (type) => {
  const typeMap = {
    'owner': { emoji: '👑', text: 'Owner' },
    'dog': { emoji: '🐕', text: 'Dog' },
    'drink': { emoji: '🍺', text: 'Drink' }
  };
  
  const membershipInfo = typeMap[type.toLowerCase()] || { emoji: '🎫', text: type.charAt(0).toUpperCase() + type.slice(1) };
  return `${membershipInfo.emoji} (${membershipInfo.text})`;
};

// Helper function to get emoji and text without parentheses for individual cards
const getMembershipDisplayCard = (type) => {
  const typeMap = {
    'owner': { emoji: '👑', text: 'Owner' },
    'dog': { emoji: '🐕', text: 'Dog' },
    'drink': { emoji: '🍺', text: 'Drink' }
  };
  
  const membershipInfo = typeMap[type.toLowerCase()] || { emoji: '🎫', text: type.charAt(0).toUpperCase() + type.slice(1) };
  return membershipInfo.emoji;
};

function MembershipAndVisits({
  activeMembershipCount,
  totalDayPasses,
  checkInCount,
  firstVisitDate,
  membershipDetails,
  membershipCounts,
  onCancelRecurringClicked,
  onMakeRecurringClicked,
  onCancelPendingCancellationClicked,
  user,
  isAuthenticated,
  cardDetails,
  setMembershipDetails
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpiredExpanded, setIsExpiredExpanded] = useState(false);
  const [showBuyPassModal, setShowBuyPassModal] = useState(false);
  const [showMakeRecurringModal, setShowMakeRecurringModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const membershipsRef = useRef(null);
  const expiredMembershipsRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [expiredHeight, setExpiredHeight] = useState(0);
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [localCardDetails, setLocalCardDetails] = useState(cardDetails);

  // Update local card details when prop changes
  useEffect(() => {
    setLocalCardDetails(cardDetails);
  }, [cardDetails]);

  useEffect(() => {
    if (isExpanded && membershipsRef.current) {
      setHeight(membershipsRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isExpanded, membershipDetails]);

  useEffect(() => {
    if (isExpiredExpanded && expiredMembershipsRef.current) {
      setExpiredHeight(expiredMembershipsRef.current.scrollHeight);
    } else {
      setExpiredHeight(0);
    }
  }, [isExpiredExpanded, membershipDetails]);

  // Add new useEffect to handle auto-expansion when no memberships
  useEffect(() => {
    const activeMemberships = getActiveMemberships(membershipDetails);
    const expiredMemberships = getExpiredMemberships(membershipDetails);
    if (activeMemberships.length === 0 && expiredMemberships.length === 0) {
      setIsExpanded(true);
    }
  }, [membershipDetails]);

  // Filter function to only show active memberships
  const getActiveMemberships = (memberships) => {
    const now = new Date();
    return memberships.filter(membership => {
      const { isRecurring, cancelPending, endDate } = membership;
      
      // If it's recurring and not pending cancellation, it's active
      if (isRecurring && !cancelPending) {
        return true;
      }
      
      // If it's not recurring, check if the end date is in the future
      if (!isRecurring && endDate) {
        return new Date(endDate) > now;
      }
      
      // If it's recurring but pending cancellation, check if end date is in the future
      if (isRecurring && cancelPending && endDate) {
        return new Date(endDate) > now;
      }
      
      return false;
    });
  };

  // Filter function to only show expired memberships
  const getExpiredMemberships = (memberships) => {
    const now = new Date();
    return memberships.filter(membership => {
      const { isRecurring, cancelPending, endDate } = membership;
      
      // If it's recurring and pending cancellation, it's expired
      if (isRecurring && cancelPending) {
        return true;
      }
      
      // If it's not recurring, check if the end date is in the past
      if (!isRecurring && endDate) {
        return new Date(endDate) < now;
      }
      
      // If it's recurring but not pending cancellation, check if end date is in the past
      if (isRecurring && !cancelPending && endDate) {
        return new Date(endDate) < now;
      }
      
      return false;
    });
  };

  // Calculate active membership counts by type
  const getActiveMembershipCounts = (memberships) => {
    const activeMemberships = getActiveMemberships(memberships);
    const counts = {};
    
    activeMemberships.forEach(membership => {
      const type = membership.type;
      counts[type] = (counts[type] || 0) + 1;
    });
    
    return Object.entries(counts).map(([type, count]) => ({ type, count }));
  };

  // Calculate expired membership counts by type
  const getExpiredMembershipCounts = (memberships) => {
    const expiredMemberships = getExpiredMemberships(memberships);
    const counts = {};
    
    expiredMemberships.forEach(membership => {
      const type = membership.type;
      counts[type] = (counts[type] || 0) + 1;
    });
    
    return Object.entries(counts).map(([type, count]) => ({ type, count }));
  };

  const handleMakeRecurringClick = (membership) => {
    setSelectedMembership(membership);
    setShowMakeRecurringModal(true);
  };

  const handleMakeRecurringConfirm = (updatedMembership) => {
    if (updatedMembership) {
      // Update the local membership details with the updated membership
      const updatedMembershipDetails = membershipDetails.map(m => 
        m._id === updatedMembership._id 
          ? { 
              ...m, 
              isRecurring: true, 
              cancelPending: false,
              nextPaymentDate: updatedMembership.nextPaymentDate || m.nextPaymentDate
            }
          : m
      );
      
      // Update parent state through setMembershipDetails
      setMembershipDetails(updatedMembershipDetails);
      
      // Update local state
      setShowMakeRecurringModal(false);
      setSelectedMembership(null);
    }
  };

  return (
    <div className="day-pass-stats-container">
      <div className="day-pass-stats-title">
        <FaIdCard className="title-icon" /> Membership & Visits
      </div>

      <div
        className="day-pass-stats-info-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <FaUserCheck className="active-memberships-icon" /> Active Memberships:
        <div className="day-pass-stats-info-answer">{getActiveMemberships(membershipDetails).length || 0}</div>
        {isExpanded ? <FaCaretUp className="caret-icon" /> : <FaCaretDown className="caret-icon" />}
      </div>

      <div
        className="day-pass-stats-memberships"
        style={{ height: isExpanded ? `${height}px` : '0' }}
        ref={membershipsRef}
      >
        <div className="membership-details">
          {getActiveMemberships(membershipDetails).length > 0 ? (
            <>
              {getActiveMembershipCounts(membershipDetails).map(({ type, count }) => (
                <div key={type} className="membership-details-title">
                  {count} {getMembershipDisplay(type)} Memberships
                </div>
              ))}

              {getActiveMemberships(membershipDetails).map((membership) => {
                const {
                  _id,
                  type,
                  isRecurring,
                  cancelPending,
                  nextPaymentDate,
                  endDate,
                } = membership;
                const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

                return (
                  <div key={_id} className="membership-detail">
                    {/* Left Column: Title */}
                    <div className="membership-detail-title">{getMembershipDisplayCard(type)}</div>

                    {/* Middle Column: Membership Information */}
                    <div className="membership-detail-middle">
                      {isRecurring && !cancelPending ? (
                        <span>
                          Next Payment:{" "}
                          {nextPaymentDate
                            ? new Date(nextPaymentDate).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      ) : isRecurring && cancelPending ? (
                        <span>
                          Membership ends on:{" "}
                          {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                        </span>
                      ) : (
                        <span>
                          Expires:{" "}
                          {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                        </span>
                      )}
                    </div>

                    {/* Right Column: Buttons */}
                    <div className="membership-detail-buttons">
                      {isRecurring && !cancelPending ? (
                        <button
                          className="cancel-recurring-button"
                          onClick={() => onCancelRecurringClicked(membership)}
                        >
                          Cancel Recurring
                        </button>
                      ) : isRecurring && cancelPending ? (
                        <button
                          className="cancel-pending-button"
                          onClick={() => onCancelPendingCancellationClicked(membership)}
                        >
                          Cancel Cancellation
                        </button>
                      ) : (
                        <button
                          className="make-recurring-button"
                          onClick={() => handleMakeRecurringClick(membership)}
                        >
                          Make Recurring
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="no-memberships-prompt">
              <p>You don't have any active memberships yet. You can purchase monthly memberships or day passes from our pass options.</p>
              {PASS_OPTIONS_COMING_SOON ? (
                <>
                  <span className="pass-options-button pass-options-button--disabled" aria-disabled="true">
                    Pass Options (Coming Soon)
                  </span>
                  <p className="no-memberships-coming-soon-note">Pass pricing will be available when we open.</p>
                </>
              ) : (
                <Link to="/Dog-Passes" className="pass-options-button">
                  Pass Options Here
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expired Memberships Section */}
      {getExpiredMemberships(membershipDetails).length > 0 && (
        <>
          <div
            className="day-pass-stats-info-header"
            onClick={() => setIsExpiredExpanded(!isExpiredExpanded)}
          >
            <FaUserCheck className="expired-memberships-icon" /> Old Memberships:
            <div className="day-pass-stats-info-answer">{getExpiredMemberships(membershipDetails).length || 0}</div>
            {isExpiredExpanded ? <FaCaretUp className="caret-icon" /> : <FaCaretDown className="caret-icon" />}
          </div>

          <div
            className="day-pass-stats-memberships expired-memberships"
            style={{ height: isExpiredExpanded ? `${expiredHeight}px` : '0' }}
            ref={expiredMembershipsRef}
          >
            <div className="membership-details">
              {getExpiredMembershipCounts(membershipDetails).map(({ type, count }) => (
                <div key={type} className="membership-details-title">
                  {count} {getMembershipDisplay(type)} Memberships
                </div>
              ))}

              {getExpiredMemberships(membershipDetails).map((membership) => {
                const {
                  _id,
                  type,
                  isRecurring,
                  cancelPending,
                  endDate,
                } = membership;
                const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

                return (
                  <div key={_id} className="membership-detail expired-membership-detail">
                    {/* Left Column: Title */}
                    <div className="membership-detail-title">{getMembershipDisplayCard(type)}</div>

                    {/* Middle Column: Membership Information */}
                    <div className="membership-detail-middle">
                      {isRecurring && cancelPending ? (
                        <span>
                          Cancelled - ended on:{" "}
                          {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                        </span>
                      ) : (
                        <span>
                          Past membership - expired on:{" "}
                          {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}
                        </span>
                      )}
                    </div>

                    {/* Right Column: No buttons for expired memberships */}
                    <div className="membership-detail-buttons">
                      {/* Empty - no buttons for expired memberships */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="day-pass-stats-info-header">
        <FaTicketAlt className="total-day-passes-icon" /> Total Day Passes:
        <div className="day-pass-stats-info-answer">{totalDayPasses || 0}</div>
      </div>

      <div className="day-pass-stats-info-header">
        <FaSignInAlt className="check-ins-icon" /> Check-ins:
        <div className="day-pass-stats-info-answer">{checkInCount || 0}</div>
      </div>

      <div className="first-visit-header">
        <FaCalendarCheck className="first-visit-icon" /> First Visit:
        <div className="first-visit-answer">{firstVisitDate || "Haven't visited yet"}</div>
      </div>

      <BuyPassModal
        isOpen={showBuyPassModal}
        onClose={() => setShowBuyPassModal(false)}
        selectedOption="Monthly Pass"
        user={user}
        isAuthenticated={isAuthenticated}
        useSavedCard={useSavedCard}
        setUseSavedCard={setUseSavedCard}
        onPaymentSuccess={() => {
          setShowBuyPassModal(false);
        }}
        onPaymentFailure={() => {
          setShowBuyPassModal(false);
        }}
      />

      {showMakeRecurringModal && stripePromise && (
        <Elements stripe={stripePromise}>
          <MakeRecurringMembershipModal
            isOpen={showMakeRecurringModal}
            membership={selectedMembership}
            onConfirm={handleMakeRecurringConfirm}
            onCancel={() => setShowMakeRecurringModal(false)}
            user={user}
            cardDetails={localCardDetails}
          />
        </Elements>
      )}
    </div>
  );
}

export default MembershipAndVisits;
